import { z } from 'zod';

export const shadcnFieldTypeSchema = z.enum([
  'text',
  'email',
  'password',
  'number',
  'select',
  'textarea',
  'date',
  'boolean',
  'url',
]);

export const shadcnActionTypeSchema = z.enum(['edit', 'delete', 'create', 'view']);

export const shadcnPlanSchema = z.enum(['free', 'pro', 'enterprise']);

export const shadcnPageTypeSchema = z.enum([
  'crud-list',
  'crud-detail',
  'form-only',
  'dashboard',
  'settings',
]);

export const shadcnFieldDefSchema = z.object({
  name: z.string().min(1),
  label: z.string().min(1),
  type: shadcnFieldTypeSchema,
  required: z.boolean().optional(),
  placeholder: z.string().optional(),
  helperText: z.string().optional(),
  options: z.array(z.object({
    label: z.string().min(1),
    value: z.string().min(1),
  })).optional(),
});

export const shadcnColumnDefSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  sortable: z.boolean().optional(),
  filterable: z.boolean().optional(),
  type: shadcnFieldTypeSchema.optional(),
});

export const shadcnTableConfigSchema = z.object({
  columns: z.array(shadcnColumnDefSchema).min(1),
  actions: z.array(shadcnActionTypeSchema).optional(),
  emptyState: z.string().optional(),
  pagination: z.boolean().optional(),
  searchable: z.boolean().optional(),
  selectable: z.boolean().optional(),
});

export const shadcnFormConfigSchema = z.object({
  fields: z.array(shadcnFieldDefSchema).min(1),
  submitLabel: z.string().optional(),
  cancelLabel: z.string().optional(),
});

export const shadcnDialogConfigSchema = z.object({
  trigger: z.enum(['edit', 'create', 'view']).optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  confirmLabel: z.string().optional(),
  cancelLabel: z.string().optional(),
  action: shadcnActionTypeSchema.optional(),
});

export const kanbanLayoutSchema = z.object({
  type: z.literal('kanban'),
  groupByField: z.string().min(1),
  swimlanes: z.array(z.object({
    value: z.string().min(1),
    label: z.string().min(1),
    color: z.string().optional(),
  })),
});

export const analyticsGridLayoutSchema = z.object({
  type: z.literal('analytics-grid'),
  columns: z.union([z.literal(12), z.literal(6), z.literal(4)]),
  widgets: z.array(z.object({
    span: z.number(),
    rowSpan: z.number().optional(),
    widgetId: z.string().min(1),
  })),
});

export const splitPanelLayoutSchema = z.object({
  type: z.literal('split-panel'),
  primary: z.enum(['table', 'list']),
  secondary: z.enum(['detail', 'form', 'preview']),
  defaultSplit: z.number(),
});

export const fullPageLayoutSchema = z.object({
  type: z.literal('full'),
});

const shadcnPageConfigBaseSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  path: z.string().min(1),
  icon: z.string().optional(),
  entity: z.string().optional(),
  type: shadcnPageTypeSchema,
  actions: z.array(shadcnActionTypeSchema).optional(),
  table: shadcnTableConfigSchema.optional(),
  form: shadcnFormConfigSchema.optional(),
  dialog: shadcnDialogConfigSchema.optional(),
});

export const tabsLayoutSchema = z.lazy(() => z.object({
  type: z.literal('tabs'),
  tabs: z.array(z.object({
    label: z.string().min(1),
    pageConfig: shadcnPageConfigSchema,
  })),
}));

export const shadcnPageConfigSchema = z.lazy(() => shadcnPageConfigBaseSchema.extend({
  layout: z.union([
    kanbanLayoutSchema,
    analyticsGridLayoutSchema,
    splitPanelLayoutSchema,
    tabsLayoutSchema,
    fullPageLayoutSchema,
  ]),
}));

export const shadcnAppConfigSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  shadcn: z.literal(true),
  plan: shadcnPlanSchema.default('free'),
  pages: z.array(shadcnPageConfigSchema).min(1),
});

export type ShadcnFieldType = z.infer<typeof shadcnFieldTypeSchema>;
export type ShadcnActionType = z.infer<typeof shadcnActionTypeSchema>;
export type ShadcnPlan = z.infer<typeof shadcnPlanSchema>;
export type ShadcnPageType = z.infer<typeof shadcnPageTypeSchema>;

export type ShadcnFieldDef = z.infer<typeof shadcnFieldDefSchema>;
export type ShadcnColumnDef = z.infer<typeof shadcnColumnDefSchema>;
export type ShadcnTableConfig = z.infer<typeof shadcnTableConfigSchema>;
export type ShadcnFormConfig = z.infer<typeof shadcnFormConfigSchema>;
export type ShadcnDialogConfig = z.infer<typeof shadcnDialogConfigSchema>;
export type KanbanLayout = z.infer<typeof kanbanLayoutSchema>;
export type AnalyticsGridLayout = z.infer<typeof analyticsGridLayoutSchema>;
export type SplitPanelLayout = z.infer<typeof splitPanelLayoutSchema>;
export type TabsLayout = z.infer<typeof tabsLayoutSchema>;
export type FullPageLayout = z.infer<typeof fullPageLayoutSchema>;
export type ShadcnPageLayout = KanbanLayout | AnalyticsGridLayout | SplitPanelLayout | TabsLayout | FullPageLayout;
export type ShadcnPageConfig = z.infer<typeof shadcnPageConfigSchema>;
export type ShadcnAppConfig = z.infer<typeof shadcnAppConfigSchema>;
